import { IOperationResultNoValue, PostStatus } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { getEntityDisplayName } from '../../../../extViewmodels/Utils';
import { IModalContext, ModalChildComponentContextKey } from '../../../../models';
import { useEventLogging } from '../../../../models/Logging';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { SocialAccountCampaignViewModel } from '../../../../viewmodels/AdminViewModels';
import { DeprecatedTextArea } from '../../../../web/components/DeprecatedTextArea';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { asModalComponent } from '../../../../web/components/Modal';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

interface IProps extends IModalContext<boolean> {
	campaigns: SocialAccountCampaignViewModel[];
	styles?: StyleDeclarationValue[];
}

function SocialApprovalReminderComposerBase({ campaigns, parentModal, styles }: IProps) {
	const errorMessages = useErrorMessages();
	const { logApiError, logEvent } = useEventLogging('SocialApprovalReminderComposer');

	const [isBusy, setIsBusy] = React.useState<boolean>(false);
	const [note, setNote] = React.useState<string>(() => {
		let value = 'Hi,';
		if (campaigns?.length === 1 && campaigns?.[0]?.status === PostStatus.Pending) {
			value = `Hi ${campaigns[0].approvalRequests[0].approver?.firstName},`;
		}
		return value;
	});

	const onTextChanged = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setNote(event.target.value || '');
	};

	const sendReminder = () => {
		setIsBusy(true);
		const promises = campaigns.map(campaign => {
			const trimmed = (note || '').trim();
			if (trimmed && campaign) {
				const promise = campaign.sendApprovalRequest();
				if (promise) {
					logEvent('SendReminder');
					return promise;
				}
			}
		});
		Promise.all(promises)
			.then(() => {
				setIsBusy(false);
				toast.success(`Reminder email${campaigns.length > 1 ? 's' : ''} sent!`);
				parentModal?.onRequestClose(true, false);
			})
			.catch((error: IOperationResultNoValue) => {
				setIsBusy(false);
				logApiError('SendReminder-Error', error);
				errorMessages.pushApiError(error);
			});
	};

	return (
		<div className={css(styleSheet.container, ...(styles || []))}>
			<div className={css(styleSheet.title)}>Notify Client</div>
			<div className={css(styleSheet.message)}>
				{`Leave a reminder for the client below. Levitate will send this along with the campaigns you've picked out `}
				<b>from your email.</b>
			</div>
			{campaigns?.length > 1 && (
				<div className={css(styleSheet.message)}>
					{campaigns.map((campaign, i) => {
						return (
							<div key={`contact${i}`}>
								{campaign.approvalRequests.length > 0
									? getEntityDisplayName(campaign.approvalRequests[0].approver)
									: ''}
							</div>
						);
					})}
				</div>
			)}
			<div>
				<DeprecatedTextArea
					autoFocus={true}
					disabled={isBusy}
					inputClassName={css(styleSheet.inputField)}
					inputId='bulk-email-approval-reminder-composer-input'
					onChange={onTextChanged}
					value={note || ''}
				/>
			</div>
			<button className={css([baseStyleSheet.ctaButton, styleSheet.button])} disabled={isBusy} onClick={sendReminder}>
				<span>Send</span>
			</button>
			{isBusy && <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />}
		</div>
	);
}

export const SocialApprovalReminderComposer = inject(ModalChildComponentContextKey)(
	observer(SocialApprovalReminderComposerBase)
);

export const SocialApprovalReminderComposerModal = asModalComponent(SocialApprovalReminderComposer, {
	className: `social-approval-reminder-composer-modal`,
	shouldCloseOnOverlayClick: true,
	useDefaultHeader: true,
});
